import { useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../lib/supabase';

/**
 * Toggle pin status on a post
 */
export function useTogglePinPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId, isPinned }: { postId: string; isPinned: boolean }) => {
      const { error } = await supabase
        .from('posts')
        .update({ is_pinned: !isPinned, updated_at: new Date().toISOString() })
        .eq('id', postId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social'] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });
}

/**
 * Hide/delete a post (soft delete)
 */
export function useHidePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId }: { postId: string }) => {
      const { error } = await supabase
        .from('posts')
        .update({ visible: false, updated_at: new Date().toISOString() })
        .eq('id', postId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social'] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });
}

/**
 * Create a new post (for gym owners/admins posting from web)
 */
export function useCreatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      content,
      companyId,
      userId,
      isCompanyWide = true,
      locationId,
    }: {
      content: string;
      companyId: string;
      userId: string;
      isCompanyWide?: boolean;
      locationId?: string;
    }) => {
      const { data, error } = await supabase
        .from('posts')
        .insert({
          content,
          company_id: companyId,
          user_id: userId,
          is_company_wide: isCompanyWide,
          location_id: locationId || null,
          visible: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social'] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });
}
